import { DateTime } from 'luxon';
import { dateTimeFormat } from './constants';

/** Moment-style tokens used in legacy call sites → Luxon `toFormat` tokens. */
const LEGACY_FORMAT_TO_LUXON: Record<string, string> = {
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'MM-DD-YYYY': 'MM-dd-yyyy',
};

function toLuxonFormat(format: string): string {
  return LEGACY_FORMAT_TO_LUXON[format] ?? format;
}

function resolveInvoiceDateInput(value: unknown): DateTime | null {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'object' && value !== null && 'startDate' in value) {
    return resolveInvoiceDateInput(
      (value as { startDate: unknown }).startDate
    );
  }

  if (value instanceof Date) {
    const dateTime = DateTime.fromJSDate(value);
    return dateTime.isValid ? dateTime : null;
  }

  if (typeof value === 'string') {
    const isoParsed = DateTime.fromISO(value, { zone: 'utc' });
    if (isoParsed.isValid) {
      return isoParsed;
    }
    const jsParsed = DateTime.fromJSDate(new Date(value));
    return jsParsed.isValid ? jsParsed : null;
  }

  return null;
}

/** API payload date (`yyyy-MM-dd`), e.g. invoice save. */
export function toApiDateString(value: unknown): string {
  const dateTime = resolveInvoiceDateInput(value);
  return dateTime ? dateTime.toFormat(dateTimeFormat.yearMonthDateFormat) : '';
}

/** Patch reactive form controls that expect a `Date`. */
export function toFormDate(value: unknown): Date | null {
  const dateTime = resolveInvoiceDateInput(value);
  return dateTime ? dateTime.toJSDate() : null;
}

/** Display / download formatting (supports legacy moment format strings). */
export function formatInvoiceDateDisplay(
  value: unknown,
  format: string = dateTimeFormat.date
): string {
  const dateTime = resolveInvoiceDateInput(value);
  return dateTime ? dateTime.toFormat(toLuxonFormat(format)) : '';
}

/** Calendar date (`yyyy-MM-dd`) → local `Date`, no timezone shift. */
export function calendarDateToFormDate(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const dateTime = DateTime.fromISO(value);
  return dateTime.isValid ? dateTime.toJSDate() : null;
}
